// src/components/admin/AdminPages.jsx
// ─── AdminPaidUsers, AdminLocations, AdminDevices,
//     AdminReferrals, AdminSettings
// All connected to real API via adminApi ────────────────────────

import { useState, useEffect, useMemo } from 'react';
import { adminApi } from '../../utils/api';

// ── Mobile CSS ────────────────────────────────────────────────
const PAGES_MOBILE_CSS = `
@media (max-width: 768px) {

  /* ── AdminLocations ── */
  .loc-grid           { grid-template-columns: 1fr !important; }

  /* ── AdminDevices ── */
  .dev-split-grid     { grid-template-columns: 1fr !important; }
  .dev-metric-row     { grid-template-columns: repeat(2, 1fr) !important; }

  /* ── AdminReferrals ── */
  /* hide less-important table cols on small screens */
  .ref-table-timezone { display: none !important; }

  /* ── AdminSettings ── */
  .settings-form-grid { grid-template-columns: 1fr !important; }
}

@media (max-width: 640px) {
  /* Referrals stats strip: 2×2 */
  .ref-stats-strip .stats-strip-chip { flex: 0 0 calc(50% - 4px) !important; }

  /* Settings: full-width save buttons */
  .settings-save-btn  { width: 100% !important; }
}

@media (max-width: 400px) {
  .dev-metric-row { grid-template-columns: 1fr !important; }
}
`;

function InjectStyle({ id, css }) {
  useEffect(() => {
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id; el.textContent = css;
    document.head.appendChild(el);
  }, []);
  return null;
}

// ── Shared helpers ────────────────────────────────────────────

function Tag({ text, bg, color }) {
  return (
    <span style={{ display:'inline-flex', alignItems:'center', padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700, background:bg, color }}>
      {text}
    </span>
  );
}

function Spinner() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:60, color:'var(--gray)', fontSize:13 }}>
      Loading…
    </div>
  );
}

function ErrMsg({ msg }) {
  return (
    <div style={{ padding:24, color:'var(--red)', fontSize:13 }}>
      {msg}
    </div>
  );
}

const PLAN_COLORS = {
  paid: { bg:'rgba(46,204,138,.12)', color:'var(--green)' },
  free: { bg:'rgba(148,163,184,.10)', color:'var(--gray)' },
};
const AVATAR_COLORS = ['var(--gold)','var(--blue)','var(--green)','#f472b6','#a78bfa'];
const initials = n => n?.split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2) || '??';

// ══════════════════════════════════════════════════════════════
// PAID USERS
// ══════════════════════════════════════════════════════════════
export function AdminPaidUsers() {
  const [data,    setData]    = useState({ users:[], total:0 });
  const [filter,  setFilter]  = useState('all');
  const [search,  setSearch]  = useState('');
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    setLoading(true);
    adminApi.getUsers({ search, plan: filter === 'all' ? undefined : filter, limit: 100 })
      .then(d  => setData(d))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [filter, search]);

  const paidCount = useMemo(() => data.users.filter(u => u.plan === 'paid').length, [data]);
  const freeCount = useMemo(() => data.users.filter(u => u.plan === 'free').length, [data]);

  if (error) return <ErrMsg msg={error}/>;

  return (
    <div style={{ padding:'14px 18px', animation:'pageIn .3s ease', display:'flex', flexDirection:'column', gap:10 }}>

      <div className="stats-strip">
        {[
          { l:'Total',    v: data.total },
          { l:'Paid',     v: paidCount, c:'var(--green)' },
          { l:'Free',     v: freeCount, c:'var(--gray)'  },
        ].map(({ l,v,c }) => (
          <div key={l} className="stats-strip-chip">
            <span className="stats-strip-label">{l}</span>
            <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:c||'var(--white)', lineHeight:1 }}>{v}</span>
          </div>
        ))}
      </div>

      <div className="filter-bar">
        <div style={{ display:'flex' }}>
          {[['all','All'],['paid','Paid'],['free','Free']].map(([v,l]) => (
            <button key={v} onClick={() => setFilter(v)} style={{
              padding:'0 16px', height:38,
              background: filter===v ? 'rgba(212,168,67,.1)' : 'transparent',
              border:'none', borderRight:'1px solid rgba(255,255,255,.04)',
              color: filter===v ? 'var(--gold)' : 'rgba(247,243,234,.55)',
              fontSize:12, fontWeight:filter===v?700:500, letterSpacing:'.03em',
              cursor:'pointer', fontFamily:"'Inter',sans-serif", transition:'all .15s',
            }}>{l}</button>
          ))}
        </div>
        <div className="filter-actions">
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search name or email…"
            style={{ background:'var(--dark3)', border:'1px solid rgba(255,255,255,.08)', color:'var(--white)', fontSize:12, padding:'5px 12px', outline:'none', fontFamily:"'Inter',sans-serif", width:200 }}
          />
        </div>
      </div>

      {loading ? <Spinner/> : (
        <div className="tbl-desktop" style={{ height:'auto', flex:'unset', overflow:'auto', maxHeight:'60vh' }}>
          <table>
            <thead>
              <tr>{['User','Email','Plan','Timezone','Account Type','Joined'].map(h => (
                <th key={h} style={{ padding:'10px 14px', fontSize:11, letterSpacing:'.05em', textTransform:'uppercase', color:'var(--gray)', fontWeight:600, background:'var(--dark3)', position:'sticky', top:0 }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {data.users.map((u, i) => (
                <tr key={u.id} className="tbl-tr">
                  <td style={{ padding:'11px 14px', borderBottom:'1px solid rgba(255,255,255,.04)' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <div style={{ width:30, height:30, borderRadius:6, flexShrink:0, background:AVATAR_COLORS[i%AVATAR_COLORS.length], display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:800, color:'var(--black)' }}>{initials(u.name)}</div>
                      <span style={{ fontSize:13, fontWeight:600, color:'var(--white)' }}>{u.name}</span>
                    </div>
                  </td>
                  <td style={{ padding:'11px 14px', fontSize:12, color:'var(--gray)', borderBottom:'1px solid rgba(255,255,255,.04)' }}>{u.email}</td>
                  <td style={{ padding:'11px 14px', borderBottom:'1px solid rgba(255,255,255,.04)' }}>
                    <Tag text={u.plan} bg={PLAN_COLORS[u.plan]?.bg} color={PLAN_COLORS[u.plan]?.color}/>
                  </td>
                  <td style={{ padding:'11px 14px', fontSize:12, color:'var(--off-white)', borderBottom:'1px solid rgba(255,255,255,.04)' }}>{u.timezone}</td>
                  <td style={{ padding:'11px 14px', fontSize:12, color:'var(--off-white)', borderBottom:'1px solid rgba(255,255,255,.04)' }}>{u.accType}</td>
                  <td style={{ padding:'11px 14px', fontSize:12, color:'var(--gray)', borderBottom:'1px solid rgba(255,255,255,.04)' }}>
                    {new Date(u.joinedAt).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' })}
                  </td>
                </tr>
              ))}
              {!data.users.length && (
                <tr><td colSpan={6} style={{ padding:40, textAlign:'center', color:'var(--gray)', fontSize:13 }}>No users found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <div style={{ fontSize:11, color:'var(--gray)', textAlign:'right' }}>Showing {data.users.length} of {data.total} users</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// LOCATIONS  —  real GPS-based country/city data
// ══════════════════════════════════════════════════════════════
export function AdminLocations() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [tab,     setTab]     = useState('countries'); // countries | cities

  useEffect(() => {
    adminApi.getLocations()
      .then(d  => setData(d))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner/>;
  if (error)   return <ErrMsg msg={error}/>;

  const countries  = data.byCountry  || [];
  const cities     = data.byCity     || [];
  const regions    = Object.entries(data.byRegion || {}).sort((a,b) => b[1]-a[1]);
  const COLORS     = ['var(--gold)','var(--blue)','var(--green)','#f472b6','#a78bfa','var(--gray)'];
  const listData   = tab === 'countries' ? countries : cities;
  const maxCount   = listData[0]?.count || 1;
  const geoTotal   = data.geoTotal   || 0;  // users who granted permission
  const noGeoCount = data.noGeoCount || 0;  // users who denied / not yet asked

  return (
    <div style={{ padding:'14px 18px', animation:'pageIn .3s ease', display:'flex', flexDirection:'column', gap:10 }}>
      <InjectStyle id="pages-mobile-css" css={PAGES_MOBILE_CSS}/>

      {/* Coverage banner */}
      <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
        {[
          { l:'GPS Located',   v:geoTotal,   c:'var(--green)', sub:'shared location' },
          { l:'Not Located',   v:noGeoCount, c:'var(--gray)',  sub:'denied or pending' },
          { l:'Countries',     v:countries.length, c:'var(--gold)',  sub:'unique countries' },
          { l:'Cities',        v:cities.length,    c:'var(--blue)',  sub:'unique cities' },
        ].map(({ l,v,c,sub }) => (
          <div key={l} style={{ flex:'1 1 120px', background:'var(--dark2)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'12px 16px', position:'relative', overflow:'hidden' }}>
            <div style={{ position:'absolute', top:0, left:0, right:0, height:2, background:`linear-gradient(90deg,${c},transparent 70%)` }}/>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'var(--gray)', marginBottom:6 }}>{l}</div>
            <div style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:26, color:c, lineHeight:1 }}>{v}</div>
            <div style={{ fontSize:10, color:'var(--gray)', marginTop:4 }}>{sub}</div>
          </div>
        ))}
      </div>

      <div className="loc-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>

        {/* Country / City bars — tabbed */}
        <div className="panel">
          <div className="panel-hd">
            <div className="panel-title"><b>Users</b> by Location</div>
            <div style={{ display:'flex' }}>
              {[['countries','Countries'],['cities','Cities']].map(([v,l]) => (
                <button key={v} onClick={() => setTab(v)} style={{
                  padding:'0 10px', height:28, fontSize:11, fontWeight:tab===v?700:500,
                  background:tab===v?'rgba(212,168,67,.1)':'transparent',
                  border:'none', borderRight:'1px solid rgba(255,255,255,.04)',
                  color:tab===v?'var(--gold)':'rgba(247,243,234,.45)',
                  cursor:'pointer', fontFamily:"'Inter',sans-serif",
                }}>{l}</button>
              ))}
            </div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:9 }}>
            {listData.slice(0,15).map(({ name, count, flag }) => (
              <div key={name} style={{ display:'flex', alignItems:'center', gap:10 }}>
                {flag && <span style={{ fontSize:16, lineHeight:1, flexShrink:0 }}>{flag}</span>}
                <span style={{ width:130, fontSize:11, color:'var(--off-white)', flexShrink:0, fontWeight:500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{name}</span>
                <div style={{ flex:1, height:5, background:'rgba(255,255,255,.06)', borderRadius:3, overflow:'hidden' }}>
                  <div style={{ height:'100%', width:`${(count/maxCount)*100}%`, background:'linear-gradient(90deg,var(--gold),var(--blue))', borderRadius:3, animation:'growBar .9s ease both' }}/>
                </div>
                <span style={{ width:30, textAlign:'right', fontSize:11, color:'var(--gray)', flexShrink:0 }}>{count}</span>
              </div>
            ))}
            {!listData.length && (
              <div style={{ color:'var(--gray)', fontSize:12, padding:'8px 0' }}>
                No GPS location data yet. Users will be prompted when they next log in.
              </div>
            )}
          </div>
        </div>

        {/* Region breakdown */}
        <div className="panel">
          <div className="panel-hd">
            <div className="panel-title"><b>Regional</b> Distribution</div>
            <span style={{ fontSize:11, color:'var(--gray)' }}>GPS-based</span>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            {regions.map(([region, count], i) => (
              <div key={region} style={{ display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ width:90, fontSize:12, color:'var(--off-white)', fontWeight:500, flexShrink:0 }}>{region}</span>
                <div style={{ flex:1, height:6, background:'rgba(255,255,255,.06)', borderRadius:3, overflow:'hidden' }}>
                  <div style={{ height:'100%', width:`${(count/(geoTotal||1))*100}%`, background:COLORS[i%COLORS.length], borderRadius:3, animation:'growBar .9s ease both' }}/>
                </div>
                <span style={{ fontSize:12, fontWeight:700, color:COLORS[i%COLORS.length], width:36, textAlign:'right', flexShrink:0 }}>{count}</span>
              </div>
            ))}
            {!regions.length && <div style={{ color:'var(--gray)', fontSize:12 }}>No region data yet.</div>}
          </div>
        </div>
      </div>

      {/* Full country table */}
      <div className="panel">
        <div className="panel-hd"><div className="panel-title"><b>Country</b> Details</div></div>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr>{['Country','City (Top)','Users','% of Located'].map(h => (
                <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontSize:11, letterSpacing:'.05em', textTransform:'uppercase', color:'var(--gray)', fontWeight:600, background:'var(--dark3)', borderBottom:'1px solid rgba(255,255,255,.05)' }}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {countries.map((row, i) => (
                <tr key={row.name} className="tbl-tr" style={{ animation:`rowSlideIn .3s ease ${i*.03}s both` }}>
                  <td style={{ padding:'12px 14px', borderBottom:'1px solid rgba(255,255,255,.04)', fontWeight:600, color:'var(--white)' }}>
                    {row.flag && <span style={{ marginRight:8 }}>{row.flag}</span>}{row.name}
                  </td>
                  <td style={{ padding:'12px 14px', borderBottom:'1px solid rgba(255,255,255,.04)', color:'var(--gray)', fontSize:12 }}>{row.topCity || '—'}</td>
                  <td style={{ padding:'12px 14px', borderBottom:'1px solid rgba(255,255,255,.04)', fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:'var(--gold)', letterSpacing:'.02em', lineHeight:1 }}>{row.count}</td>
                  <td style={{ padding:'12px 14px', borderBottom:'1px solid rgba(255,255,255,.04)', color:'var(--gray)' }}>
                    {geoTotal > 0 ? ((row.count/geoTotal)*100).toFixed(1) : '0.0'}%
                  </td>
                </tr>
              ))}
              {!countries.length && (
                <tr><td colSpan={4} style={{ padding:40, textAlign:'center', color:'var(--gray)', fontSize:13 }}>No GPS data yet — users will be prompted on next login.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// DEVICES
// ══════════════════════════════════════════════════════════════
export function AdminDevices() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    adminApi.getDevices()
      .then(d  => setData(d))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner/>;
  if (error)   return <ErrMsg msg={error}/>;

  const DEV_COLORS = { Mobile:'#38bdf8', Desktop:'#6c63ff', Tablet:'#f472b6', Unknown:'var(--gray)' };
  const OS_COLORS  = { iOS:'#6c63ff', Android:'#34d399', Windows:'#38bdf8', macOS:'#fbbf24', Linux:'#94a3b8', Other:'var(--gray)' };

  return (
    <div style={{ padding:'14px 18px', animation:'pageIn .3s ease', display:'flex', flexDirection:'column', gap:10 }}>
      <div className="dev-metric-row metric-row" style={{ gridTemplateColumns:'repeat(3,1fr)' }}>
        {(data.devices || []).filter(d => d.type !== 'Unknown').map(d => (
          <div key={d.type} className="mcard" style={{ '--mc-color': DEV_COLORS[d.type] }}>
            <div className="mcard-label">{d.type}</div>
            <div className="mcard-val" style={{ color:DEV_COLORS[d.type], fontSize:36 }}>{d.count.toLocaleString()}</div>
            <div className="mcard-sub">{d.pct}% of users</div>
          </div>
        ))}
      </div>

      <div className="dev-split-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
        <div className="panel">
          <div className="panel-hd"><div className="panel-title"><b>Device</b> Type Split</div></div>
          <div style={{ display:'flex', height:10, borderRadius:5, overflow:'hidden', gap:2, marginBottom:12 }}>
            {(data.devices||[]).map(d => (
              <div key={d.type} style={{ width:`${d.pct}%`, background:DEV_COLORS[d.type]||'var(--gray)', transition:'width .8s' }}/>
            ))}
          </div>
          {(data.devices||[]).map(d => (
            <div key={d.type} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
              <span style={{ width:8, height:8, borderRadius:2, background:DEV_COLORS[d.type]||'var(--gray)', flexShrink:0 }}/>
              <span style={{ fontSize:12, color:'var(--off-white)', width:70 }}>{d.type}</span>
              <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:DEV_COLORS[d.type]||'var(--gray)', lineHeight:1 }}>{d.pct}%</span>
              <span style={{ fontSize:11, color:'var(--gray)' }}>({d.count})</span>
            </div>
          ))}
        </div>
        <div className="panel">
          <div className="panel-hd"><div className="panel-title"><b>OS</b> Breakdown</div></div>
          {(data.os||[]).map(o => (
            <div key={o.name} style={{ marginBottom:12 }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                <span style={{ fontSize:12, color:'var(--off-white)', fontWeight:500 }}>{o.name}</span>
                <span style={{ fontSize:11, color:OS_COLORS[o.name]||'var(--gray)', fontWeight:700 }}>{o.pct}%</span>
              </div>
              <div style={{ height:5, background:'rgba(255,255,255,.06)', borderRadius:3, overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${o.pct}%`, background:OS_COLORS[o.name]||'var(--gray)', borderRadius:3, animation:'growBar .9s ease both' }}/>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// REFERRALS
// ══════════════════════════════════════════════════════════════
export function AdminReferrals() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [status,  setStatus]  = useState('all');

  useEffect(() => {
    setLoading(true);
    adminApi.getReferrals({ status, limit: 50 })
      .then(d  => setData(d))
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [status]);

  if (error) return <ErrMsg msg={error}/>;

  const s = data?.summary || {};

  return (
    <div style={{ padding:'14px 18px', animation:'pageIn .3s ease', display:'flex', flexDirection:'column', gap:10 }}>
      <div className="ref-stats-strip stats-strip">
        {[
          { l:'Total Referrals', v:s.total     || 0, c:'var(--gold)'  },
          { l:'Converted',       v:s.converted  || 0, c:'var(--green)' },
          { l:'Conv. Rate',      v:`${s.convRate||0}%`, c:'var(--blue)' },
          { l:'Commission Paid', v:`$${(s.totalPaid||0).toFixed(2)}`, c:'var(--gold)' },
        ].map(({ l,v,c }) => (
          <div key={l} className="stats-strip-chip">
            <span className="stats-strip-label">{l}</span>
            <span style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:18, color:c, lineHeight:1 }}>{v}</span>
          </div>
        ))}
      </div>

      {/* Leaderboard */}
      <div className="panel">
        <div className="panel-hd">
          <div className="panel-title"><b>Top</b> Referrers</div>
        </div>
        {loading ? <Spinner/> : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr>{['Rank','Referrer','Total Refs','Earned'].map(h => (
                  <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontSize:11, letterSpacing:'.05em', textTransform:'uppercase', color:'var(--gray)', fontWeight:600, background:'var(--dark3)', borderBottom:'1px solid rgba(255,255,255,.05)' }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {(data?.leaderboard||[]).map((row, i) => (
                  <tr key={i} className="tbl-tr">
                    <td style={{ padding:'12px 14px', borderBottom:'1px solid rgba(255,255,255,.04)', fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:i===0?'var(--gold)':i===1?'var(--off-white)':'var(--gray)', letterSpacing:'.03em' }}>#{i+1}</td>
                    <td style={{ padding:'12px 14px', borderBottom:'1px solid rgba(255,255,255,.04)', fontWeight:600, color:'var(--white)' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ width:28, height:28, borderRadius:6, background:AVATAR_COLORS[i%AVATAR_COLORS.length], display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:800, color:'var(--black)', flexShrink:0 }}>
                          {initials(row.user?.name)}
                        </div>
                        <div>
                          <div style={{ fontSize:12, fontWeight:600 }}>{row.user?.name || 'Unknown'}</div>
                          <div style={{ fontSize:11, color:'var(--gray)' }}>{row.user?.email || ''}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding:'12px 14px', borderBottom:'1px solid rgba(255,255,255,.04)', fontFamily:"'Bebas Neue',sans-serif", fontSize:20, color:'var(--gold)', lineHeight:1 }}>{row.totalReferrals}</td>
                    <td style={{ padding:'12px 14px', borderBottom:'1px solid rgba(255,255,255,.04)', fontWeight:700, color:'var(--green)' }}>${row.totalEarned.toFixed(2)}</td>
                  </tr>
                ))}
                {!data?.leaderboard?.length && (
                  <tr><td colSpan={4} style={{ padding:40, textAlign:'center', color:'var(--gray)', fontSize:13 }}>No referrals yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* All referrals table */}
      <div className="panel">
        <div className="panel-hd">
          <div className="panel-title"><b>All</b> Referrals</div>
          <div style={{ display:'flex', gap:0 }}>
            {[['all','All'],['PENDING','Pending'],['ACTIVE','Active'],['PAID','Paid']].map(([v,l]) => (
              <button key={v} onClick={() => setStatus(v)} style={{
                padding:'0 12px', height:32, background:status===v?'rgba(212,168,67,.1)':'transparent',
                border:'none', borderRight:'1px solid rgba(255,255,255,.04)',
                color:status===v?'var(--gold)':'rgba(247,243,234,.55)',
                fontSize:11, fontWeight:600, cursor:'pointer', fontFamily:"'Inter',sans-serif",
              }}>{l}</button>
            ))}
          </div>
        </div>
        {loading ? <Spinner/> : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
              <thead>
                <tr>{['Referrer','Referee','Status','Commission','Date'].map(h => (
                  <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontSize:11, letterSpacing:'.05em', textTransform:'uppercase', color:'var(--gray)', fontWeight:600, background:'var(--dark3)', borderBottom:'1px solid rgba(255,255,255,.05)' }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {(data?.referrals||[]).map((r, i) => (
                  <tr key={r.id} className="tbl-tr">
                    <td style={{ padding:'12px 14px', borderBottom:'1px solid rgba(255,255,255,.04)', fontWeight:600, color:'var(--white)' }}>{r.referrer?.name || '—'}</td>
                    <td style={{ padding:'12px 14px', borderBottom:'1px solid rgba(255,255,255,.04)', color:'var(--off-white)' }}>{r.referee?.name || '—'}</td>
                    <td style={{ padding:'12px 14px', borderBottom:'1px solid rgba(255,255,255,.04)' }}>
                      <Tag text={r.status}
                        bg={r.status==='ACTIVE'?'rgba(46,204,138,.12)':r.status==='PENDING'?'rgba(251,191,36,.12)':'rgba(148,163,184,.1)'}
                        color={r.status==='ACTIVE'?'var(--green)':r.status==='PENDING'?'var(--warning)':'var(--gray)'}
                      />
                    </td>
                    <td style={{ padding:'12px 14px', borderBottom:'1px solid rgba(255,255,255,.04)', fontWeight:700, color:'var(--green)' }}>
                      ${Number(r.commissionEarned||0).toFixed(2)}
                    </td>
                    <td style={{ padding:'12px 14px', borderBottom:'1px solid rgba(255,255,255,.04)', color:'var(--gray)', fontSize:11 }}>
                      {new Date(r.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
                {!data?.referrals?.length && (
                  <tr><td colSpan={5} style={{ padding:40, textAlign:'center', color:'var(--gray)', fontSize:13 }}>No referrals found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// SETTINGS
// ══════════════════════════════════════════════════════════════
export function AdminSettings({ user: propUser }) {
  const [profile,  setProfile]  = useState({ name: propUser?.name || '', email: propUser?.email || '' });
  const [passwords, setPasswords] = useState({ currentPassword:'', newPassword:'', confirmPassword:'' });
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState(null);
  const [err,      setErr]      = useState(null);

  const saveProfile = async () => {
    if (!profile.name || !profile.email) { setErr('Name and email are required.'); return; }
    setSaving(true); setErr(null); setMsg(null);
    try {
      await adminApi.updateProfile({ name: profile.name, email: profile.email });
      setMsg('Profile saved successfully.');
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const savePassword = async () => {
    if (passwords.newPassword !== passwords.confirmPassword) { setErr('Passwords do not match.'); return; }
    if (passwords.newPassword.length < 8) { setErr('New password must be at least 8 characters.'); return; }
    setSaving(true); setErr(null); setMsg(null);
    try {
      await adminApi.updatePassword({ currentPassword: passwords.currentPassword, newPassword: passwords.newPassword });
      setMsg('Password updated. You will be logged out of other devices.');
      setPasswords({ currentPassword:'', newPassword:'', confirmPassword:'' });
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const fieldStyle = {
    width:'100%', background:'var(--dark3)', border:'1px solid rgba(255,255,255,.08)',
    color:'var(--white)', fontSize:13, padding:'10px 14px', outline:'none',
    fontFamily:"'Inter',sans-serif", borderRadius:'var(--radius)',
  };

  const SCard = ({ title, icon, children }) => (
    <div style={{ background:'var(--dark2)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:22, marginBottom:14, position:'relative', overflow:'hidden' }}>
      <div style={{ position:'absolute', top:0, left:0, right:0, height:1, background:'linear-gradient(90deg,var(--gold-dim),transparent 60%)', opacity:.4 }}/>
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:18, paddingBottom:14, borderBottom:'1px solid var(--border)' }}>
        <span style={{ fontSize:16 }}>{icon}</span>
        <span style={{ fontSize:13, fontWeight:700, color:'var(--white)' }}>{title}</span>
      </div>
      {children}
    </div>
  );

  return (
    <div style={{ padding:'14px 18px 60px', animation:'pageIn .3s ease', maxWidth:820 }}>
      <InjectStyle id="pages-mobile-css" css={PAGES_MOBILE_CSS}/>
      {msg && <div style={{ padding:'10px 16px', background:'rgba(46,204,138,.1)', border:'1px solid rgba(46,204,138,.25)', color:'var(--green)', fontSize:13, marginBottom:14, borderRadius:'var(--radius)' }}>{msg}</div>}
      {err && <div style={{ padding:'10px 16px', background:'rgba(217,79,79,.1)', border:'1px solid rgba(217,79,79,.25)', color:'var(--red)', fontSize:13, marginBottom:14, borderRadius:'var(--radius)' }}>{err}</div>}

      <SCard title="Profile Information" icon="👤">
        <div className="settings-form-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
          {[['Name','text',profile.name,'name'],['Email','email',profile.email,'email']].map(([l,t,v,k]) => (
            <div key={k}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, letterSpacing:'.06em', textTransform:'uppercase', color:'var(--gray)', marginBottom:6 }}>{l}</label>
              <input type={t} value={v} style={fieldStyle} onChange={e => setProfile(p => ({ ...p, [k]: e.target.value }))}/>
            </div>
          ))}
        </div>
        <button className="settings-save-btn" onClick={saveProfile} disabled={saving}
          style={{ padding:'9px 22px', background:saving?'rgba(255,255,255,.1)':'linear-gradient(135deg,var(--gold),var(--gold-dim))', border:'none', color:saving?'var(--gray)':'var(--black)', fontSize:11, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase', fontFamily:"'Inter',sans-serif", cursor:saving?'not-allowed':'pointer' }}>
          {saving ? 'Saving…' : 'Save Profile'}
        </button>
      </SCard>

      <SCard title="Change Password" icon="🔒">
        <div className="settings-form-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
          {[['Current Password','currentPassword'],['New Password','newPassword'],['Confirm New Password','confirmPassword']].map(([l,k]) => (
            <div key={k}>
              <label style={{ display:'block', fontSize:11, fontWeight:600, letterSpacing:'.06em', textTransform:'uppercase', color:'var(--gray)', marginBottom:6 }}>{l}</label>
              <input type="password" value={passwords[k]} placeholder="••••••••" style={fieldStyle} onChange={e => setPasswords(p => ({ ...p, [k]: e.target.value }))}/>
            </div>
          ))}
        </div>
        <button className="settings-save-btn" onClick={savePassword} disabled={saving}
          style={{ padding:'9px 22px', background:saving?'rgba(255,255,255,.1)':'linear-gradient(135deg,var(--gold),var(--gold-dim))', border:'none', color:saving?'var(--gray)':'var(--black)', fontSize:11, fontWeight:700, letterSpacing:'.06em', textTransform:'uppercase', fontFamily:"'Inter',sans-serif", cursor:saving?'not-allowed':'pointer' }}>
          {saving ? 'Saving…' : 'Update Password'}
        </button>
      </SCard>
    </div>
  );
}